import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/utils/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { RefreshCw, Server, Cpu, Activity, AlertCircle, ChevronDown, RotateCcw, FileText } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';
import { useWiseboardRefresh } from '../context/WiseboardRefreshContext';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('WorkerStatusBar', 'wiseboard');

interface DoclingHealth {
  state: 'healthy' | 'degraded_capable' | 'warming' | 'unhealthy' | 'disabled' | 'unknown';
  detail: string | null;
  extraction_capable: boolean | null;
  checked_at: string | null;
  last_capable_at: string | null;
  unhealthy_since: string | null;
  readiness: string | null;
  auto_heal_enabled: boolean;
  auto_restarts_last_hour: number;
  last_auto_restart_at: string | null;
}

interface WorkerStatus {
  id: string;
  worker_id: string;
  hostname: string | null;
  status: 'online' | 'offline' | 'processing' | 'error' | 'restarting' | 'stopping' | 'starting';
  last_heartbeat: string | null;
  current_job_id: string | null;
  queue_depth: number;
  jobs_processed: number;
  last_error: string | null;
  restart_requested: boolean;
  version: string | null;
  gpu_id: string | null;
  enabled: boolean;
  overflow_threshold: number | null;
  metadata?: { docling?: DoclingHealth } | null;
}

const STATUS_CONFIG = {
  online: { color: 'bg-green-500', label: 'Online', textColor: 'text-green-600' },
  offline: { color: 'bg-red-500', label: 'Offline', textColor: 'text-red-600' },
  processing: { color: 'bg-blue-500', label: 'Processing', textColor: 'text-blue-600' },
  error: { color: 'bg-yellow-500', label: 'Error', textColor: 'text-yellow-600' },
  restarting: { color: 'bg-orange-500', label: 'Restarting', textColor: 'text-orange-600' },
  stopping: { color: 'bg-orange-400', label: 'Stopping...', textColor: 'text-orange-500' },
  starting: { color: 'bg-cyan-500', label: 'Starting...', textColor: 'text-cyan-600' }
};

interface WorkerConfig {
  name: string;
  icon: typeof Server;
  description: string;
  gpu?: string;
  isPrimary?: boolean;
  isOverflow?: boolean;
}

const WORKER_CONFIG: Record<string, WorkerConfig> = {
  // Dual GPU workers - each handles Docling extraction + Claude classification
  document_worker_gpu1: {
    name: 'GPU1 (Overflow)',
    icon: Cpu,
    description: 'RTX 5070 - Docling extraction + Classification',
    gpu: 'RTX 5070',
    isOverflow: true
  },
  document_worker_gpu2: {
    name: 'GPU2 (Primary)',
    icon: Cpu,
    description: 'RTX 3080 Ti - Docling extraction + Classification',
    gpu: 'RTX 3080 Ti',
    isPrimary: true
  }
};

// Workers to hide from UI (internal services)
const HIDDEN_WORKERS = ['watcher_gpu1', 'watcher_gpu2'];

// GPU document workers heartbeat every 30s in push-wake mode (worker-service/push_wake.py)
// and every 5s in legacy polling. 90s = three missed push-mode beats, so a live worker never
// flaps to "offline" between beats; the watcher uses the same window.
const WORKER_STALE_MS = 90000;

// The watcher heartbeats every 30s (service_watcher.py). A dead watcher must read as
// "unknown", never green: its last metadata would otherwise claim healthy forever.
const WATCHER_STALE_MS = 90000;

const DOCLING_LABEL: Record<DoclingHealth['state'], string> = {
  healthy: 'Healthy',
  degraded_capable: 'CPU fallback (serving)',
  warming: 'Starting up',
  unhealthy: 'NOT extracting',
  disabled: 'Stopped (GPU off)',
  unknown: 'Unknown'
};

/** States where Docling cannot extract and someone should look. */
function isDoclingAlarming(docling: DoclingHealth | null): boolean {
  return docling === null || docling.state === 'unhealthy' || docling.state === 'unknown';
}

async function getCurrentOrganizationId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    return null;
  }

  const { data: securityContext } = await supabase
    .from('auth_security_context')
    .select('organization_id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (securityContext?.organization_id) {
    return securityContext.organization_id;
  }

  const { data: profile, error } = await supabase
    .from('users')
    .select('organization_id')
    .eq('id', user.id)
    .single();

  if (error) {
    throw error;
  }

  return profile?.organization_id ?? null;
}

function isStale(lastHeartbeat: string | null, thresholdMs = WORKER_STALE_MS): boolean {
  if (!lastHeartbeat) {
return true;
}
  const heartbeatTime = new Date(lastHeartbeat).getTime();
  const now = Date.now();
  return now - heartbeatTime > thresholdMs;
}

/**
 * A stale heartbeat means the worker is gone, whatever it last claimed to be
 * doing. This previously only downgraded 'online', so a worker parked at
 * 'processing' rendered a healthy blue tile indefinitely — GPU1 sat that way
 * for 3 days during the 2026-07-31 Docling outage. Explicit transient states
 * are left alone so a real restart still reads as a restart.
 */
const LIVE_STATUSES = ['online', 'processing'] as const;

function effectiveWorkerStatus(worker: WorkerStatus, stale: boolean): WorkerStatus['status'] {
  if (stale && (LIVE_STATUSES as readonly string[]).includes(worker.status)) {
    return 'offline';
  }
  return worker.status;
}

/** Docling health for a GPU worker, read from its watcher's row. */
function doclingFor(workerId: string, workers: WorkerStatus[]): DoclingHealth | null {
  const gpuId = workerId.replace('document_worker_', '');
  const watcher = workers.find((w) => w.worker_id === `watcher_${gpuId}`);
  if (!watcher || isStale(watcher.last_heartbeat, WATCHER_STALE_MS)) {
    return null; // monitor offline -> unknown, never trusted-green
  }
  return watcher.metadata?.docling ?? null;
}

type ConfirmKind = 'restart-all' | 'restart-one' | 'reset-stuck';
interface ConfirmState {
  kind: ConfirmKind;
  workerId?: string;
}

export function WorkerStatusBar() {
  const [workers, setWorkers] = useState<WorkerStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [restarting, setRestarting] = useState<string | null>(null);
  const [stuckJobsCount, setStuckJobsCount] = useState(0);
  const [pendingQueueCount, setPendingQueueCount] = useState(0);
  const [togglingWorker, setTogglingWorker] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // Auto-extract toggle state
  const [autoExtractEnabled, setAutoExtractEnabled] = useState(false);
  const [togglingAutoExtract, setTogglingAutoExtract] = useState(false);

  const fetchWorkerStatus = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('worker_status')
        .select('*')
        .order('worker_id');

      if (error) {
        throw error;
      }
      setWorkers(data || []);
    } catch (err) {
      log.error('Failed to fetch worker status', {}, err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStuckJobsCount = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_stuck_queue_jobs_count');
      if (!error && data !== null) {
        setStuckJobsCount(data);
      }
    } catch {
      // Silently fail - non-critical
    }
  }, []);

  const fetchPendingQueueCount = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_pending_queue_count');
      if (!error && data !== null) {
        setPendingQueueCount(data);
      }
    } catch {
      // Silently fail - non-critical
    }
  }, []);

  const refreshWorkerPanel = useCallback(async () => {
    await Promise.all([fetchWorkerStatus(), fetchStuckJobsCount(), fetchPendingQueueCount()]);
  }, [fetchWorkerStatus, fetchStuckJobsCount, fetchPendingQueueCount]);

  const { registerRefresh } = useWiseboardRefresh();
  useEffect(() => registerRefresh(refreshWorkerPanel), [registerRefresh, refreshWorkerPanel]);

  useEffect(() => {
    // Initial fetch
    fetchWorkerStatus();
    fetchStuckJobsCount();
    fetchPendingQueueCount();
    fetchAutoExtractSetting();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('worker_status_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'worker_status'
        },
        (payload) => {
          log.info('Worker status update received', { payload });
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            setWorkers((prev) => {
              const updated = payload.new as WorkerStatus;
              const existing = prev.findIndex((w) => w.worker_id === updated.worker_id);
              if (existing >= 0) {
                const newWorkers = [...prev];
                newWorkers[existing] = updated;
                return newWorkers;
              }
              return [...prev, updated];
            });
          }
        }
      )
      .subscribe();

    // Refresh every 10 seconds to update "last seen" times
    const interval = setInterval(fetchWorkerStatus, 10000);
    // Check for stuck jobs every 30 seconds
    const stuckInterval = setInterval(fetchStuckJobsCount, 30000);
    // Check pending queue every 5 seconds for overflow logic display
    const pendingInterval = setInterval(fetchPendingQueueCount, 5000);

    return () => {
      channel.unsubscribe();
      clearInterval(interval);
      clearInterval(stuckInterval);
      clearInterval(pendingInterval);
    };
  }, [fetchWorkerStatus, fetchStuckJobsCount, fetchPendingQueueCount]);

  async function fetchAutoExtractSetting() {
    try {
      const organizationId = await getCurrentOrganizationId();
      if (!organizationId) {
        return;
      }

      const { data: org, error } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', organizationId)
        .single();

      if (error) {
throw error;
}

      const enabled = org?.settings?.auto_extract_enabled ?? false;
      setAutoExtractEnabled(enabled);
    } catch (err) {
      log.error('Failed to fetch auto-extract setting', {}, err as Error);
    }
  }

  async function toggleAutoExtract() {
    try {
      setTogglingAutoExtract(true);
      const newEnabled = !autoExtractEnabled;
      log.info('Toggling auto-extract', { newEnabled });

      const organizationId = await getCurrentOrganizationId();
      if (!organizationId) {
        throw new Error('Organization not found');
      }

      // Get current settings to preserve other values
      const { data: org } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', organizationId)
        .single();

      const currentSettings = org?.settings || {};
      const newSettings = { ...currentSettings, auto_extract_enabled: newEnabled };

      // Update organization settings
      const { error } = await supabase
        .from('organizations')
        .update({ settings: newSettings })
        .eq('id', organizationId);

      if (error) {
throw error;
}

      // Optimistic update
      setAutoExtractEnabled(newEnabled);
      log.info('Auto-extract toggled successfully', { enabled: newEnabled });
    } catch (err) {
      log.error('Failed to toggle auto-extract', {}, err as Error);
    } finally {
      setTogglingAutoExtract(false);
    }
  }

  async function toggleWorkerEnabled(workerId: string, currentEnabled: boolean) {
    try {
      setTogglingWorker(workerId);
      const newEnabled = !currentEnabled;
      log.info('Toggling worker enabled', { workerId, newEnabled });

      const { error } = await supabase.rpc('set_worker_enabled', {
        p_worker_id: workerId,
        p_enabled: newEnabled
      });

      if (error) {
throw error;
}

      // Optimistic update - watcher will update actual status
      setWorkers((prev) =>
        prev.map((w) => (w.worker_id === workerId ? { ...w, enabled: newEnabled } : w))
      );

      log.info('Worker toggled successfully', { workerId, enabled: newEnabled });
    } catch (err) {
      log.error('Failed to toggle worker', { workerId }, err as Error);
    } finally {
      setTogglingWorker(null);
    }
  }

  async function requestRestart(workerId: string) {
    try {
      setRestarting(workerId);
      log.info('Requesting restart', { workerId });

      const { error } = await supabase.rpc('request_worker_restart', {
        p_worker_id: workerId
      });

      if (error) {
throw error;
}

      log.info('Restart requested successfully', { workerId });
    } catch (err) {
      log.error('Failed to request restart', { workerId }, err as Error);
    } finally {
      setTimeout(() => setRestarting(null), 5000);
    }
  }

  async function resetStuckJobs() {
    try {
      setRestarting('reset');
      log.info('Resetting stuck queue jobs');

      const { data, error } = await supabase.rpc('reset_stuck_queue_jobs');

      if (error) {
throw error;
}

      log.info('Reset stuck jobs completed', { count: data });
      setStuckJobsCount(0); // Optimistic update
      fetchStuckJobsCount(); // Verify actual count
    } catch (err) {
      log.error('Failed to reset stuck jobs', {}, err as Error);
    } finally {
      setTimeout(() => setRestarting(null), 2000);
    }
  }

  async function restartBoth() {
    try {
      setRestarting('both');
      log.info('Requesting restart for all GPU workers');

      // Only restart actual GPU workers, not watchers or other internal services
      const gpuWorkerIds = workers
        .filter((w) => w.worker_id.startsWith('document_worker_gpu'))
        .map((w) => w.worker_id);

      if (gpuWorkerIds.length === 0) {
        log.warn('No GPU workers found to restart');
        return;
      }

      const errors: Error[] = [];

      for (const workerId of gpuWorkerIds) {
        const { error } = await supabase.rpc('request_worker_restart', {
          p_worker_id: workerId
        });

        if (error) {
          errors.push(error);
        }
      }

      if (errors.length > 0) {
        throw errors[0];
      }

      log.info('Restart requested for GPU workers', { workerIds: gpuWorkerIds });
    } catch (err) {
      log.error('Failed to restart GPU workers', {}, err as Error);
    } finally {
      setTimeout(() => setRestarting(null), 5000);
    }
  }

  if (loading) {
    return (
      <Card className="mb-4">
        <CardContent className="py-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Activity className="h-4 w-4 animate-pulse" />
            <span className="text-sm">Loading worker status...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no workers found, show a message
  if (workers.length === 0) {
    return (
      <Card className="mb-4 border-yellow-200 bg-yellow-50">
        <CardContent className="py-3">
          <div className="flex items-center gap-2 text-yellow-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">No workers registered. Start the services to see status.</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Docling can be dead while the service, the socket and the heartbeat all
  // look healthy — surface it explicitly rather than relying on the tile dot.
  const doclingAlerts = workers
    .filter((w) => w.worker_id.startsWith('document_worker_gpu') && w.enabled !== false)
    .map((w) => ({
      name: WORKER_CONFIG[w.worker_id]?.name ?? w.worker_id,
      docling: doclingFor(w.worker_id, workers)
    }))
    .filter((entry) => isDoclingAlarming(entry.docling));

  return (
    <>
      {doclingAlerts.length > 0 && (
        <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          {doclingAlerts.map((entry) => (
            <div key={entry.name} className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="text-sm">
                {entry.docling === null ? (
                  <>
                    <strong>{entry.name}:</strong> Docling health unknown — monitor offline
                  </>
                ) : (
                  <>
                    <strong>{entry.name}:</strong> Docling is not extracting
                    {entry.docling.detail ? ` (${entry.docling.detail})` : ''}
                    {entry.docling.auto_restarts_last_hour > 0
                      ? ` — ${entry.docling.auto_restarts_last_hour} auto-restart(s) in the last hour`
                      : ''}
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {stuckJobsCount > 0 && (
        <div className="mb-2 flex items-center justify-between rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2">
          <div className="flex items-center gap-2 text-yellow-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">
              {stuckJobsCount} job{stuckJobsCount !== 1 ? 's' : ''} stuck for &gt;10 min
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7 border-yellow-300 bg-white text-yellow-700 hover:bg-yellow-100"
            onClick={() => setConfirm({ kind: 'reset-stuck' })}
            disabled={restarting === 'reset'}
          >
            {restarting === 'reset' ? (
              <RefreshCw className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <RotateCcw className="h-3 w-3 mr-1" />
            )}
            Reset Now
          </Button>
        </div>
      )}
      <Card className="mb-4">
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
            {workers.filter((w) => !HIDDEN_WORKERS.includes(w.worker_id)).map((worker) => {
              const config = WORKER_CONFIG[worker.worker_id] || {
                name: worker.worker_id,
                icon: Server,
                description: 'Unknown worker'
              };
              const Icon = config.icon;
              const stale = isStale(worker.last_heartbeat);
              const effectiveStatus = effectiveWorkerStatus(worker, stale);
              const statusConfig = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG.offline;
              const isGpuWorker = worker.worker_id.startsWith('document_worker_gpu');
              const isOverflowWorker = config.isOverflow;
              const workerEnabled = worker.enabled !== false; // Default to true
              const docling = isGpuWorker ? doclingFor(worker.worker_id, workers) : null;
              // A worker that heartbeats happily while Docling is dead is the
              // exact blind spot that hid the 2026-07-31 outage for 3 days.
              const doclingAlarm = isGpuWorker && workerEnabled && isDoclingAlarming(docling);
              const dotColor = doclingAlarm ? 'bg-red-500' : statusConfig.color;

              return (
                <TooltipProvider key={worker.worker_id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className={`flex items-center gap-2 ${!workerEnabled ? 'opacity-50' : ''}`}>
                        <div className="relative">
                          <Icon className={`h-5 w-5 ${doclingAlarm ? 'text-red-600' : statusConfig.textColor}`} />
                          <span
                            data-testid={`worker-status-dot-${worker.worker_id}`}
                            className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ${dotColor} ring-2 ring-white`}
                          />
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium">{config.name}</span>
                            {config.isPrimary && (
                              <Badge variant="default" className="text-[10px] px-1 py-0 h-4">
                                Primary
                              </Badge>
                            )}
                            {isOverflowWorker && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                                Overflow
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {worker.last_heartbeat
                              ? formatDistanceToNow(new Date(worker.last_heartbeat), { addSuffix: true })
                              : 'Never'}
                          </span>
                        </div>
                        {worker.jobs_processed > 0 && (
                          // Always rendered. This is a cumulative count, so gating it on
                          // effectiveStatus === 'processing' made it blink in and out with
                          // each job and hid the busier worker whenever it happened to be
                          // idle — GPU1 at 412 showed nothing while GPU2 at 281 showed a
                          // badge. Live activity is the dot's job, not this number's.
                          //
                          // "handled", NOT "done": job_processing_loop increments
                          // jobs_processed unconditionally after process_job() returns, and
                          // process_job() swallows failures via handle_job_error() rather
                          // than raising. So failures and each retry of the same document
                          // all count. Labelling it "done" would make a failing worker
                          // display a rising success count.
                          <Badge variant="secondary" className="text-xs">
                            {worker.jobs_processed} handled
                          </Badge>
                        )}
                        {isOverflowWorker && pendingQueueCount > 0 && (
                          <Badge
                            variant={pendingQueueCount > (worker.overflow_threshold || 5) ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            Queue: {pendingQueueCount}
                          </Badge>
                        )}
                        {isGpuWorker && (
                          <Switch
                            checked={workerEnabled}
                            onCheckedChange={() => toggleWorkerEnabled(worker.worker_id, workerEnabled)}
                            disabled={togglingWorker === worker.worker_id}
                            className="ml-1"
                          />
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <div className="space-y-1 text-xs">
                        <p>
                          <strong>Status:</strong>{' '}
                          {stale && (LIVE_STATUSES as readonly string[]).includes(worker.status)
                            ? `${STATUS_CONFIG[worker.status]?.label ?? worker.status}, heartbeat stale`
                            : statusConfig.label}
                        </p>
                        {isGpuWorker && (
                          <p className={doclingAlarm ? 'text-red-500' : undefined}>
                            <strong>Docling:</strong>{' '}
                            {docling ? DOCLING_LABEL[docling.state] : 'Unknown (monitor offline)'}
                            {docling?.detail ? ` — ${docling.detail}` : ''}
                          </p>
                        )}
                        {docling?.last_capable_at && docling.state !== 'healthy' && (
                          <p>
                            <strong>Docling last OK:</strong>{' '}
                            {formatDistanceToNow(new Date(docling.last_capable_at), { addSuffix: true })}
                          </p>
                        )}
                        {docling && docling.auto_restarts_last_hour > 0 && (
                          <p>
                            <strong>Docling auto-restarts (1h):</strong>{' '}
                            {docling.auto_restarts_last_hour}
                          </p>
                        )}
                        {docling && !docling.auto_heal_enabled && (
                          <p className="text-yellow-500"><strong>Docling auto-heal:</strong> OFF</p>
                        )}
                        <p><strong>Enabled:</strong> {workerEnabled ? 'Yes' : 'No'}</p>
                        <p><strong>Host:</strong> {worker.hostname || 'Unknown'}</p>
                        {config.gpu && <p><strong>GPU:</strong> {config.gpu}</p>}
                        <p>
                          <strong>Jobs Handled:</strong> {worker.jobs_processed}
                          <span className="text-muted-foreground">
                            {' '}(attempts since last worker restart — includes retries and failures)
                          </span>
                        </p>
                        {worker.version && <p><strong>Version:</strong> {worker.version}</p>}
                        {isOverflowWorker && worker.overflow_threshold && (
                          <p><strong>Overflow Threshold:</strong> {worker.overflow_threshold} pending jobs</p>
                        )}
                        {worker.last_error && (
                          <p className="text-red-500"><strong>Last Error:</strong> {worker.last_error}</p>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>

          {/* Auto-Extract Toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 border-l pl-4 ml-2">
                  <FileText className={`h-4 w-4 ${autoExtractEnabled ? 'text-green-600' : 'text-muted-foreground'}`} />
                  <Label htmlFor="auto-extract" className="text-sm font-medium cursor-pointer">
                    Auto-Extract
                  </Label>
                  <Switch
                    id="auto-extract"
                    checked={autoExtractEnabled}
                    onCheckedChange={toggleAutoExtract}
                    disabled={togglingAutoExtract}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="text-xs">
                  {autoExtractEnabled
                    ? 'Enabled: Downloaded Encompass documents are automatically queued for GPU text extraction'
                    : 'Disabled: Documents must be manually queued for extraction'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" disabled={restarting !== null}>
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${restarting ? 'animate-spin' : ''}`} />
                AI Services
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* GPU Worker restart options */}
              {workers.some((w) => w.worker_id === 'document_worker_gpu2') && (
                <DropdownMenuItem
                  onClick={() => setConfirm({ kind: 'restart-one', workerId: 'document_worker_gpu2' })}
                >
                  <Server className="h-4 w-4 mr-2" />
                  Restart GPU2 (Primary)
                </DropdownMenuItem>
              )}
              {workers.some((w) => w.worker_id === 'document_worker_gpu1') && (
                <DropdownMenuItem
                  onClick={() => setConfirm({ kind: 'restart-one', workerId: 'document_worker_gpu1' })}
                >
                  <Server className="h-4 w-4 mr-2" />
                  Restart GPU1 (Overflow)
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setConfirm({ kind: 'restart-all' })}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Restart All GPU Workers
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setConfirm({ kind: 'reset-stuck' })}
                className="flex items-center justify-between"
              >
                <div className="flex items-center">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset Stuck Jobs
                </div>
                {stuckJobsCount > 0 && (
                  <Badge variant="destructive" className="ml-2 text-xs">
                    {stuckJobsCount}
                  </Badge>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>

    <AlertDialog
      open={confirm !== null}
      onOpenChange={(open) => {
        if (!open) {
          setConfirm(null);
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {confirm?.kind === 'restart-all' && 'Restart all GPU workers?'}
            {confirm?.kind === 'restart-one' && `Restart ${confirm.workerId}?`}
            {confirm?.kind === 'reset-stuck' && 'Reset stuck queue jobs?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {confirm?.kind === 'restart-all' && 'Both GPU workers will be restarted. In-flight extractions may be interrupted.'}
            {confirm?.kind === 'restart-one' && 'The selected GPU worker will be restarted. Any extraction it is processing will be interrupted.'}
            {confirm?.kind === 'reset-stuck' && `Resets queue jobs stuck for more than 10 minutes back to "pending" so they will be re-processed.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              const c = confirm;
              setConfirm(null);
              if (!c) {
return;
}
              if (c.kind === 'restart-all') {
void restartBoth();
} else if (c.kind === 'restart-one' && c.workerId) {
void requestRestart(c.workerId);
} else if (c.kind === 'reset-stuck') {
void resetStuckJobs();
}
            }}
          >
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
